/**
 * Noticing that somebody arrived on a password-reset link.
 *
 * In plain words: this is the app's ear. It listens for the two ways a reset can announce
 * itself and writes down that this browser is part-way through one. Everything that acts on
 * that answer, the route guard and the reset page, reads it from `recovery.ts`.
 *
 * This is the ONLY module in the recovery feature that imports the auth client, and that is
 * deliberate. `@/services/auth` reaches `@/integrations/supabase/client`, which builds a
 * database client at import time from `import.meta.env.VITE_SUPABASE_URL`. There is no
 * `.env` under Vitest, so anything that pulls it in throws `supabaseUrl is required` before
 * a single assertion runs. Keeping the listening half here leaves `recovery.ts` a leaf that
 * the guard, the page and their tests can all import freely.
 *
 * Requirements: FR-005 (the app treats the visitor as mid-reset), FR-012 (the Supabase
 * client still turns the link into a session; what was missing is anyone requiring a new
 * password afterwards).
 */

import { authClient } from "@/services/auth";
import { BOOT_HASH, beginRecovery, endRecovery, recoveryUserFromUrl } from "./recovery";

/**
 * Start watching. Call once, at the top of the app. Returns the unsubscribe.
 *
 * Two signals, because each covers what the other misses:
 *
 *  - The URL the app started on. Read from `BOOT_HASH`, the snapshot taken when
 *    `recovery.ts` was first evaluated, because supabase-js blanks `window.location.hash`
 *    as soon as it has read the tokens out of it. Reading the live hash here would be a
 *    race against that blanking. The link's own token names whose reset it is, so the reset
 *    belongs to that one person from the first moment (FR-013, D-045).
 *  - The `PASSWORD_RECOVERY` event. The client verifies the token over the network first,
 *    so this lands after the app has mounted, carrying the session it built. It fires once,
 *    and this function runs from a React effect, so on a slow phone it can be missed. The URL
 *    signal does not depend on it.
 *
 * Either one starts the reset. Only an explicit `SIGNED_OUT` ends it.
 */
export function initRecoveryDetection(): () => void {
  // Owned from the start. The record never applies to anybody else, so a link that signs
  // nobody in cannot catch the next person on a shared browser, and a link the client skips
  // cannot hand a password form to whoever is already signed in (D-045).
  const linkUser = recoveryUserFromUrl(BOOT_HASH);
  if (linkUser) beginRecovery(linkUser);

  return authClient.onAuthStateChange((event, session) => {
    if (event === "PASSWORD_RECOVERY") {
      beginRecovery(session?.user?.id);
      return;
    }

    // ONLY an explicit sign-out ends the reset, and never SIGNED_IN.
    // `providers/supabase.ts` maps every event it does not recognise to 'SIGNED_IN', and
    // `INITIAL_SESSION` is one of those: it fires on every page load. Clearing here would
    // therefore wipe the flag each time the app starts, and the user would land on the
    // dashboard with the password still unset, which is exactly the hole SC-002 closes.
    if (event === "SIGNED_OUT") {
      endRecovery();
    }
  });
}
